import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { FiPlus, FiTrash2 } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import type {
  ContragentAddressDTO,
  ContragentBankAccountDTO,
  ContragentContactDTO,
  ContragentDeliveryWindowDTO,
  ContragentLookupFromGUSResponseDTO,
  ContragentPayload,
  VATWhiteListSearchResponse,
  VATWhiteListVerification,
} from "../../../api/contragents"
import { ContragentService } from "../../../api/contragents"
import type { UserPublic } from "../../../client"
import AccountManagerSelect from "./AccountManagerSelect"
import {
  ADDRESS_TYPE_OPTIONS,
  CONTRAGENT_ROLE_OPTIONS,
  CONTRAGENT_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  DELIVERY_DAY_OPTIONS,
  getContragentRoleLabel,
  INCOTERMS_OPTIONS,
  LANGUAGE_OPTIONS,
} from "./contragentOptions"

type Props = {
  initialValue?: ContragentPayload
  isPending?: boolean
  submitLabel: string
  formId?: string
  onSubmit: (payload: ContragentPayload) => void
  onCancel?: () => void
  showSubmitActions?: boolean
  contragentId?: string
  onDirtyChange?: (isDirty: boolean) => void
  extraTabs?: Array<{
    key: string
    label: string
    content: React.ReactNode
  }>
}

type GUSDiffRow = {
  key: string
  label: string
  currentValue: string
  nextValue: string
  changed: boolean
}

const emptyAddress = (): ContragentAddressDTO => ({
  type: "LEGAL",
  street: "",
  building_number: "",
  postal_code: "",
  city: "",
  country_code: "PL",
})

const emptyBankAccount = (): ContragentBankAccountDTO => ({
  iban: "",
  swift: "",
})

const canonicalBankIdentity = (value: string): string => {
  const compact = value.replace(/[\s-]/g, "").toUpperCase()
  return compact.startsWith("PL") ? compact.slice(2) : compact
}

const canonicalNIPIdentity = (value?: string): string =>
  (value ?? "").replace(/\D/g, "")

const clearBankVerification = (
  account: ContragentBankAccountDTO,
): ContragentBankAccountDTO => {
  const {
    is_whitelisted: _isWhitelisted,
    last_verification_date: _lastVerificationDate,
    verification_request_id: _verificationRequestID,
    verification_nip: _verificationNIP,
    verification_query_date: _verificationQueryDate,
    verified_at: _verifiedAt,
    ...writable
  } = account
  return writable
}

const emptyDeliveryWindow = (): ContragentDeliveryWindowDTO => ({
  day: "MON-FRI",
  from: "08:00",
  to: "16:00",
})

const emptyContact = (): ContragentContactDTO => ({
  name: "",
  role: "",
  email: "",
  phone: "",
  is_primary: false,
})

function formatAddressPreview(address?: Partial<ContragentAddressDTO>): string {
  if (!address) return ""
  return [
    address.street,
    address.building_number,
    address.apartment_number,
    address.postal_code,
    address.city,
    address.country_code,
  ]
    .filter((value) => Boolean(value && String(value).trim()))
    .join(", ")
}

function normalizeInitialValue(
  initialValue?: ContragentPayload,
  defaultAccountManager?: string,
): ContragentPayload {
  if (!initialValue) {
    return {
      type: "ORGANIZATION",
      roles: [],
      full_name: "",
      account_manager: defaultAccountManager ?? "",
      addresses: [emptyAddress()],
      contacts: [],
      bank_accounts: [emptyBankAccount()],
      delivery_windows: [emptyDeliveryWindow()],
      preferred_language: "pl",
      default_currency: "PLN",
    }
  }

  return {
    ...initialValue,
    roles: initialValue.roles ?? [],
    account_manager:
      initialValue.account_manager || defaultAccountManager || "",
    addresses:
      initialValue.addresses && initialValue.addresses.length > 0
        ? initialValue.addresses
        : [emptyAddress()],
    contacts: initialValue.contacts ?? [],
    bank_accounts:
      initialValue.bank_accounts && initialValue.bank_accounts.length > 0
        ? initialValue.bank_accounts
        : [emptyBankAccount()],
    delivery_windows:
      initialValue.delivery_windows && initialValue.delivery_windows.length > 0
        ? initialValue.delivery_windows
        : [emptyDeliveryWindow()],
    preferred_language: initialValue.preferred_language ?? "pl",
    default_currency: initialValue.default_currency ?? "PLN",
  }
}

function cleanPayloadForComparison(
  payload: ContragentPayload,
  defaultAccountManager?: string,
): Record<string, any> {
  const cleanStr = (val?: string) => {
    if (!val) return undefined
    const trimmed = val.trim()
    return trimmed || undefined
  }

  const cleanDigits = (val?: string) => {
    if (!val) return undefined
    const digits = val.replace(/\D/g, "")
    return digits || undefined
  }

  const accountMgr =
    cleanStr(payload.account_manager) ?? cleanStr(defaultAccountManager)

  const cleanedAddresses = (payload.addresses ?? [])
    .map((addr) => ({
      type: addr.type || "LEGAL",
      street: cleanStr(addr.street) ?? "",
      building_number: cleanStr(addr.building_number) ?? "",
      apartment_number: cleanStr(addr.apartment_number),
      postal_code: cleanStr(addr.postal_code) ?? "",
      city: cleanStr(addr.city) ?? "",
      country_code: (cleanStr(addr.country_code) ?? "PL").toUpperCase(),
      gln: cleanStr(addr.gln),
      teryt_code: cleanStr(addr.teryt_code),
    }))
    .filter((a) => a.street || a.building_number || a.postal_code || a.city)

  const cleanedContacts = (payload.contacts ?? [])
    .map((c) => ({
      name: cleanStr(c.name),
      role: cleanStr(c.role),
      email: cleanStr(c.email),
      phone: cleanStr(c.phone),
      is_primary: Boolean(c.is_primary),
    }))
    .filter((c) => c.name || c.email || c.phone)

  const cleanedBanks = (payload.bank_accounts ?? [])
    .map((b) => ({
      iban: (cleanStr(b.iban) ?? "").replace(/\s/g, "").toUpperCase(),
      swift: cleanStr(b.swift),
    }))
    .filter((b) => b.iban)

  const cleanedWindows = (payload.delivery_windows ?? [])
    .map((w) => ({
      day: cleanStr(w.day) ?? "",
      from: cleanStr(w.from) ?? "",
      to: cleanStr(w.to) ?? "",
    }))
    .filter((w) => w.day && w.from && w.to)

  const cleanedRoles = [...(payload.roles ?? [])]
    .map((r) => r.trim().toUpperCase())
    .filter(Boolean)
    .sort()

  return {
    full_name: cleanStr(payload.full_name) ?? "",
    short_name: cleanStr(payload.short_name),
    type: payload.type || "ORGANIZATION",
    account_manager: accountMgr,
    legal_form: cleanStr(payload.legal_form),
    external_id: cleanStr(payload.external_id),
    nip: cleanDigits(payload.nip),
    vat_ue: cleanStr(payload.vat_ue),
    regon: cleanDigits(payload.regon),
    krs: cleanDigits(payload.krs),
    bdo: cleanDigits(payload.bdo),
    eori: cleanStr(payload.eori),
    incoterms: cleanStr(payload.incoterms),
    default_currency: (
      cleanStr(payload.default_currency) ?? "PLN"
    ).toUpperCase(),
    preferred_language: (
      cleanStr(payload.preferred_language) ?? "pl"
    ).toLowerCase(),
    payment_terms_days: payload.payment_terms_days ?? undefined,
    roles: cleanedRoles,
    addresses: cleanedAddresses,
    contacts: cleanedContacts,
    bank_accounts: cleanedBanks,
    delivery_windows: cleanedWindows,
  }
}

export default function ContragentForm({
  initialValue,
  isPending = false,
  submitLabel,
  formId,
  onSubmit,
  onCancel,
  showSubmitActions = true,
  contragentId,
  onDirtyChange,
  extraTabs = [],
}: Props) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const defaultAccountManager =
    currentUser?.fullName || currentUser?.email || ""

  const { isOpen, onOpen, onClose } = useDisclosure()
  const vatModal = useDisclosure()
  const [activeTab, setActiveTab] = useState(0)
  const [form, setForm] = useState<ContragentPayload>(() =>
    normalizeInitialValue(initialValue, defaultAccountManager),
  )

  const initialNormalized = useMemo(
    () => normalizeInitialValue(initialValue, defaultAccountManager),
    [initialValue, defaultAccountManager],
  )

  useEffect(() => {
    const cleanForm = cleanPayloadForComparison(form, defaultAccountManager)
    const cleanInitial = cleanPayloadForComparison(
      initialNormalized,
      defaultAccountManager,
    )
    const dirty = JSON.stringify(cleanForm) !== JSON.stringify(cleanInitial)
    onDirtyChange?.(dirty)
  }, [form, initialNormalized, defaultAccountManager, onDirtyChange])

  const [gusLookupResult, setGUSLookupResult] =
    useState<ContragentLookupFromGUSResponseDTO | null>(null)
  const [isGUSLoading, setIsGUSLoading] = useState(false)
  const [vatResult, setVATResult] = useState<VATWhiteListSearchResponse | null>(
    null,
  )
  const [vatSelected, setVATSelected] = useState<string[]>([])
  const [isVATLoading, setIsVATLoading] = useState(false)
  const [verifyingAccount, setVerifyingAccount] = useState<string | null>(null)
  const [historyByAccount, setHistoryByAccount] = useState<
    Record<string, VATWhiteListVerification[]>
  >({})

  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Europe/Warsaw",
  })
  const [vatQueryDate, setVATQueryDate] = useState(today)

  const searchVATAccounts = async () => {
    if (!form.nip) {
      toast({ title: "Wprowadź NIP przed wyszukiwaniem", status: "warning" })
      return
    }
    setIsVATLoading(true)
    try {
      const result = await ContragentService.searchVATWhiteList(
        form.nip,
        vatQueryDate,
      )
      setVATResult(result)
      setVATSelected([])
      vatModal.onOpen()
    } catch (error: any) {
      toast({
        title: "Nie udało się pobrać rachunków",
        description: error?.error?.error ?? error?.message,
        status: "error",
      })
    } finally {
      setIsVATLoading(false)
    }
  }

  const addSelectedVATAccounts = () => {
    const existing = new Set(
      form.bank_accounts.map((account) =>
        account.iban.replace(/[\s-]/g, "").toUpperCase(),
      ),
    )
    const additions = vatSelected
      .filter((iban) => !existing.has(iban.replace(/[\s-]/g, "").toUpperCase()))
      .map((iban) => ({ iban, swift: "" }))
    setForm((current) => ({
      ...current,
      bank_accounts: [
        ...current.bank_accounts.filter((account) => account.iban.trim()),
        ...additions,
      ],
    }))
    vatModal.onClose()
  }

  const verifyVATAccount = async (index: number) => {
    const account = form.bank_accounts[index]
    if (!contragentId) return
    setVerifyingAccount(account.iban)
    try {
      const evidence = await ContragentService.verifyVATWhiteList(
        contragentId,
        account.iban,
        vatQueryDate,
      )
      setForm((current) => ({
        ...current,
        bank_accounts: current.bank_accounts.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                is_whitelisted: evidence.account_assigned,
                last_verification_date: evidence.checked_at,
                verified_at: evidence.checked_at,
                verification_request_id: evidence.request_id,
                verification_nip: evidence.nip,
                verification_query_date: evidence.query_date,
              }
            : item,
        ),
      }))
      toast({
        title: evidence.account_assigned
          ? "Rachunek jest na białej liście"
          : "Rachunku nie ma na białej liście",
        status: evidence.account_assigned ? "success" : "warning",
      })
    } catch (error: any) {
      toast({
        title: "Weryfikacja nie powiodła się",
        description: error?.error?.error ?? error?.message,
        status: "error",
      })
    } finally {
      setVerifyingAccount(null)
    }
  }

  const loadVATAccountHistory = async (iban: string) => {
    if (!contragentId) return
    try {
      const history = await ContragentService.listVATWhiteListVerifications(
        contragentId,
        iban,
      )
      setHistoryByAccount((current) => ({ ...current, [iban]: history }))
    } catch (error: any) {
      toast({
        title: "Nie udało się pobrać historii",
        description: error?.error?.error ?? error?.message,
        status: "error",
      })
    }
  }

  const gusBoxBg = useColorModeValue(
    gusLookupResult?.mocked ? "orange.50" : "teal.50",
    gusLookupResult?.mocked ? "orange.900" : "teal.900",
  )
  const gusBoxBorder = useColorModeValue(
    gusLookupResult?.mocked ? "orange.200" : "teal.200",
    gusLookupResult?.mocked ? "orange.700" : "teal.700",
  )
  const gusTableHeadBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const gusRowChangedBg = useColorModeValue("green.50", "green.900")
  const gusMutedText = useColorModeValue("gray.600", "gray.400")
  const gusMockNoteColor = useColorModeValue("orange.700", "orange.200")

  const errors = useMemo(() => {
    const next: Record<string, string> = {}
    if (!form.full_name.trim()) next.full_name = "Nazwa pełna jest wymagana"
    if (!form.roles.length) next.roles = "Wybierz co najmniej jedną rolę"
    if (
      form.nip &&
      !/^[A-Za-z0-9]+$/.test(form.nip.trim().replace(/[\s.-]+/g, ""))
    )
      next.nip = "Identyfikator podatkowy może zawierać tylko litery i cyfry"
    if (
      form.addresses.some(
        (address) => !address.city.trim() || !address.country_code.trim(),
      )
    ) {
      next.addresses = "Każdy adres musi mieć miasto i kraj"
    }
    return next
  }, [form])

  const firstErrorTab = useMemo(() => {
    if (errors.full_name || errors.roles) return 0
    if (errors.nip) return 1
    if (errors.addresses) return 2
    return 0
  }, [errors])

  const tabHasError = useMemo(
    () => ({
      basic: Boolean(errors.full_name || errors.roles),
      identification: Boolean(errors.nip),
      logistics: Boolean(errors.addresses),
      finance: false,
    }),
    [errors],
  )
  const normalizedNIP = (form.nip ?? "").replace(/\D/g, "")
  const canLookupGUS = normalizedNIP.length === 10

  const updateAddress = (
    index: number,
    patch: Partial<ContragentAddressDTO>,
  ) => {
    setForm((current) => ({
      ...current,
      addresses: current.addresses.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }))
  }

  const updateBank = (
    index: number,
    patch: Partial<ContragentBankAccountDTO>,
  ) => {
    setForm((current) => ({
      ...current,
      bank_accounts: current.bank_accounts.map((item, itemIndex) => {
        if (itemIndex !== index) return item
        const next = { ...item, ...patch }
        if (
          patch.iban !== undefined &&
          canonicalBankIdentity(patch.iban) !== canonicalBankIdentity(item.iban)
        ) {
          return clearBankVerification(next)
        }
        return next
      }),
    }))
  }

  const updateNIP = (nip: string) => {
    setForm((current) => ({
      ...current,
      nip,
      bank_accounts:
        canonicalNIPIdentity(nip) === canonicalNIPIdentity(current.nip)
          ? current.bank_accounts
          : current.bank_accounts.map(clearBankVerification),
    }))
  }

  const updateWindow = (
    index: number,
    patch: Partial<ContragentDeliveryWindowDTO>,
  ) => {
    setForm((current) => ({
      ...current,
      delivery_windows: current.delivery_windows.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }))
  }

  const updateContact = (
    index: number,
    patch: Partial<ContragentContactDTO>,
  ) => {
    setForm((current) => ({
      ...current,
      contacts: (current.contacts ?? []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }))
  }

  const addContact = () => {
    setForm((current) => ({
      ...current,
      contacts: [...(current.contacts ?? []), emptyContact()],
    }))
  }

  const removeContact = (index: number) => {
    setForm((current) => ({
      ...current,
      contacts: (current.contacts ?? []).filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    }))
  }

  const handlePostalCodeLookup = async (index: number) => {
    const postalCode = form.addresses[index]?.postal_code?.trim()
    if (!postalCode) {
      toast({
        title: "Podaj kod pocztowy",
        status: "warning",
      })
      return
    }

    try {
      const result = await ContragentService.lookupPostalCode(postalCode)
      updateAddress(index, {
        postal_code: result.postal_code,
        city: result.city || form.addresses[index]?.city || "",
        street: result.street || form.addresses[index]?.street || "",
        country_code: result.country_code || "PL",
      })
      toast({
        title: "Uzupełniono dane adresu",
        status: "success",
      })
    } catch (error: any) {
      toast({
        title: "Nie udało się pobrać adresu",
        description: error?.body?.error ?? error?.message ?? "Błąd",
        status: "error",
      })
    }
  }

  const handleGUSLookup = async () => {
    if (!canLookupGUS) {
      toast({
        title: "Podaj poprawny 10-cyfrowy NIP",
        status: "warning",
      })
      return
    }

    try {
      setIsGUSLoading(true)
      const result = await ContragentService.lookupByGUS(normalizedNIP)
      setGUSLookupResult(result)
      onOpen()
      toast({
        title: result.mocked
          ? "Pobrano dane testowe z modułu GUS"
          : "Pobrano dane kontrahenta z GUS",
        description: "Zweryfikuj dane przed zastosowaniem.",
        status: "success",
      })
    } catch (error: any) {
      setGUSLookupResult(null)
      toast({
        title: "Nie udało się pobrać danych z GUS",
        description: error?.body?.error ?? error?.message ?? "Błąd",
        status: "error",
      })
    } finally {
      setIsGUSLoading(false)
    }
  }

  const applyGUSPrefill = () => {
    if (!gusLookupResult) return
    setForm((current) => {
      const nextNIP = gusLookupResult.prefill.nip ?? current.nip
      return {
        ...current,
        type: gusLookupResult.prefill.type,
        full_name: gusLookupResult.prefill.full_name || current.full_name,
        short_name: gusLookupResult.prefill.short_name ?? current.short_name,
        legal_form: gusLookupResult.prefill.legal_form ?? current.legal_form,
        country_code:
          gusLookupResult.prefill.country_code ?? current.country_code,
        nip: nextNIP,
        regon: gusLookupResult.prefill.regon ?? current.regon,
        krs: gusLookupResult.prefill.krs ?? current.krs,
        bdo: gusLookupResult.prefill.bdo ?? current.bdo,
        addresses:
          gusLookupResult.prefill.addresses.length > 0
            ? gusLookupResult.prefill.addresses
            : current.addresses,
        roles:
          current.roles.length > 0
            ? current.roles
            : gusLookupResult.prefill.roles,
        primary_email:
          gusLookupResult.prefill.primary_email ?? current.primary_email,
        bank_accounts:
          canonicalNIPIdentity(nextNIP) === canonicalNIPIdentity(current.nip)
            ? current.bank_accounts
            : current.bank_accounts.map(clearBankVerification),
      }
    })
    onClose()
    toast({
      title: "Dane z GUS zostały wpisane do formularza",
      description: "Zmiany nie zostały jeszcze zapisane.",
      status: "success",
    })
  }

  const gusDiffRows: GUSDiffRow[] = gusLookupResult
    ? [
        {
          key: "full_name",
          label: "Nazwa pełna",
          currentValue: form.full_name ?? "",
          nextValue: gusLookupResult.prefill.full_name ?? "",
          changed:
            (form.full_name ?? "") !==
            (gusLookupResult.prefill.full_name ?? ""),
        },
        {
          key: "type",
          label: "Typ",
          currentValue: form.type ?? "",
          nextValue: gusLookupResult.prefill.type ?? "",
          changed: (form.type ?? "") !== (gusLookupResult.prefill.type ?? ""),
        },
        {
          key: "nip",
          label: "NIP",
          currentValue: form.nip ?? "",
          nextValue: gusLookupResult.prefill.nip ?? "",
          changed: (form.nip ?? "") !== (gusLookupResult.prefill.nip ?? ""),
        },
        {
          key: "regon",
          label: "REGON",
          currentValue: form.regon ?? "",
          nextValue: gusLookupResult.prefill.regon ?? "",
          changed: (form.regon ?? "") !== (gusLookupResult.prefill.regon ?? ""),
        },
        {
          key: "krs",
          label: "KRS",
          currentValue: form.krs ?? "",
          nextValue: gusLookupResult.prefill.krs ?? "",
          changed: (form.krs ?? "") !== (gusLookupResult.prefill.krs ?? ""),
        },
        {
          key: "bdo",
          label: "BDO",
          currentValue: form.bdo ?? "",
          nextValue: gusLookupResult.prefill.bdo ?? "",
          changed: (form.bdo ?? "") !== (gusLookupResult.prefill.bdo ?? ""),
        },
        {
          key: "address",
          label: "Adres prawny",
          currentValue: formatAddressPreview(form.addresses[0]),
          nextValue: formatAddressPreview(gusLookupResult.prefill.addresses[0]),
          changed:
            formatAddressPreview(form.addresses[0]) !==
            formatAddressPreview(gusLookupResult.prefill.addresses[0]),
        },
      ]
    : []

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (Object.keys(errors).length > 0) {
      setActiveTab(firstErrorTab)
      return
    }

    onSubmit({
      ...form,
      full_name: form.full_name.trim(),
      short_name: form.short_name?.trim() || undefined,
      account_manager: form.account_manager?.trim() || undefined,
      legal_form: form.legal_form?.trim() || undefined,
      external_id: form.external_id?.trim() || undefined,
      nip: form.nip?.replace(/\D/g, "") || undefined,
      vat_ue: form.vat_ue?.trim() || undefined,
      regon: form.regon?.replace(/\D/g, "") || undefined,
      krs: form.krs?.replace(/\D/g, "") || undefined,
      bdo: form.bdo?.replace(/\D/g, "") || undefined,
      eori: form.eori?.trim() || undefined,
      incoterms: form.incoterms?.trim() || undefined,
      default_currency: form.default_currency?.trim() || undefined,
      preferred_language: form.preferred_language?.trim() || undefined,
      addresses: form.addresses.map((address) => ({
        ...address,
        street: address.street.trim(),
        building_number: address.building_number.trim(),
        apartment_number: address.apartment_number?.trim() || undefined,
        postal_code: address.postal_code.trim(),
        city: address.city.trim(),
        country_code: address.country_code.trim().toUpperCase(),
        gln: address.gln?.trim() || undefined,
        teryt_code: address.teryt_code?.trim() || undefined,
      })),
      contacts: (form.contacts ?? [])
        .map((c) => ({
          name: c.name?.trim() || undefined,
          role: c.role?.trim() || undefined,
          email: c.email?.trim() || undefined,
          phone: c.phone?.trim() || undefined,
          is_primary: Boolean(c.is_primary),
        }))
        .filter((c) => c.name || c.email || c.phone),
      bank_accounts: form.bank_accounts
        .map((account) => ({
          ...account,
          iban: account.iban.trim(),
          swift: account.swift?.trim() || undefined,
        }))
        .filter((account) => account.iban),
      delivery_windows: form.delivery_windows
        .map((window) => ({
          ...window,
          day: window.day.trim(),
          from: window.from.trim(),
          to: window.to.trim(),
        }))
        .filter((window) => window.day && window.from && window.to),
    })
  }

  return (
    <VStack
      as="form"
      id={formId}
      onSubmit={handleSubmit}
      align="stretch"
      spacing={6}
    >
      <Tabs
        variant="enclosed"
        colorScheme="green"
        isLazy
        index={activeTab}
        onChange={setActiveTab}
      >
        <TabList overflowX="auto" overflowY="hidden">
          <Tab color={tabHasError.basic ? "red.500" : undefined}>
            Dane podstawowe
          </Tab>
          <Tab color={tabHasError.identification ? "red.500" : undefined}>
            Identyfikacja
          </Tab>
          <Tab color={tabHasError.logistics ? "red.500" : undefined}>
            Adresy i dostawy
          </Tab>
          <Tab color={tabHasError.finance ? "red.500" : undefined}>Finanse</Tab>
          {extraTabs.map((tab) => (
            <Tab key={tab.key}>{tab.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={6}>
            <Grid
              templateColumns={{
                base: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
              }}
              gap={4}
            >
              <GridItem>
                <FormControl isRequired isInvalid={!!errors.full_name}>
                  <FormLabel>Nazwa pełna</FormLabel>
                  <Input
                    size="sm"
                    value={form.full_name}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        full_name: event.target.value,
                      }))
                    }
                    placeholder="Wpisz nazwę pełną"
                  />
                  <FormErrorMessage>{errors.full_name}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Nazwa skrócona</FormLabel>
                  <Input
                    size="sm"
                    value={form.short_name ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        short_name: event.target.value,
                      }))
                    }
                    placeholder="Opcjonalnie"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Typ</FormLabel>
                  <Select
                    size="sm"
                    value={form.type}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        type: event.target.value as ContragentPayload["type"],
                      }))
                    }
                  >
                    {CONTRAGENT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Opiekun konta</FormLabel>
                  <AccountManagerSelect
                    size="sm"
                    value={form.account_manager ?? ""}
                    onChange={(nextValue) =>
                      setForm((current) => ({
                        ...current,
                        account_manager: nextValue,
                      }))
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 2 }}>
                <FormControl isInvalid={!!errors.roles}>
                  <FormLabel>Role</FormLabel>
                  <CheckboxGroup
                    value={form.roles}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        roles: value as string[],
                      }))
                    }
                  >
                    <HStack spacing={4} wrap="wrap">
                      {CONTRAGENT_ROLE_OPTIONS.map((role) => (
                        <Checkbox key={role} value={role}>
                          {getContragentRoleLabel(role)}
                        </Checkbox>
                      ))}
                    </HStack>
                  </CheckboxGroup>
                  <FormErrorMessage>{errors.roles}</FormErrorMessage>
                </FormControl>
              </GridItem>
            </Grid>
            <Box mt={6} pt={6} borderTopWidth="1px">
              <HStack justify="space-between" mb={4} align="center">
                <Heading size="sm">Osoby kontaktowe</Heading>
                <Button
                  leftIcon={<FiPlus />}
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                  onClick={addContact}
                >
                  Dodaj kontakt
                </Button>
              </HStack>
              {(form.contacts ?? []).length === 0 ? (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">
                  Brak osób kontaktowych. Kliknij „Dodaj kontakt”, aby dodać
                  osobę.
                </Text>
              ) : (
                <Stack spacing={4}>
                  {(form.contacts ?? []).map((contact, index) => (
                    <Box
                      key={`contact-${index}`}
                      borderWidth="1px"
                      borderRadius="lg"
                      p={4}
                    >
                      <HStack justify="space-between" mb={3} align="center">
                        <HStack spacing={2}>
                          <Text fontWeight="semibold" fontSize="sm">
                            Kontakt {index + 1}
                          </Text>
                          {contact.is_primary && (
                            <Badge colorScheme="teal" fontSize="xs">
                              Główny kontakt
                            </Badge>
                          )}
                        </HStack>
                        <IconButton
                          aria-label="Usuń kontakt"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeContact(index)}
                        />
                      </HStack>
                      <Grid
                        templateColumns={{
                          base: "minmax(0, 1fr)",
                          md: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(4, minmax(0, 1fr))",
                        }}
                        gap={4}
                      >
                        <FormControl>
                          <FormLabel fontSize="xs">Imię i nazwisko</FormLabel>
                          <Input
                            size="sm"
                            value={contact.name ?? ""}
                            onChange={(e) =>
                              updateContact(index, { name: e.target.value })
                            }
                            placeholder="Jan Kowalski"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">Stanowisko / Rola</FormLabel>
                          <Input
                            size="sm"
                            value={contact.role ?? ""}
                            onChange={(e) =>
                              updateContact(index, { role: e.target.value })
                            }
                            placeholder="Manager ds. zakupów"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">E-mail</FormLabel>
                          <Input
                            size="sm"
                            type="email"
                            value={contact.email ?? ""}
                            onChange={(e) =>
                              updateContact(index, { email: e.target.value })
                            }
                            placeholder="jan.kowalski@example.com"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">Telefon</FormLabel>
                          <Input
                            size="sm"
                            value={contact.phone ?? ""}
                            onChange={(e) =>
                              updateContact(index, { phone: e.target.value })
                            }
                            placeholder="+48 123 456 789"
                          />
                        </FormControl>
                      </Grid>
                      <Checkbox
                        mt={3}
                        size="sm"
                        isChecked={contact.is_primary ?? false}
                        onChange={(e) =>
                          updateContact(index, { is_primary: e.target.checked })
                        }
                      >
                        Główna osoba kontaktowa
                      </Checkbox>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </TabPanel>

          <TabPanel px={0} pt={6}>
            <Grid
              templateColumns={{
                base: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(3, minmax(0, 1fr))",
              }}
              gap={4}
            >
              <FormControl>
                <FormLabel>ID zewnętrzne</FormLabel>
                <Input
                  size="sm"
                  value={form.external_id ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      external_id: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Forma prawna</FormLabel>
                <Input
                  size="sm"
                  value={form.legal_form ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      legal_form: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl isInvalid={!!errors.nip}>
                <FormLabel>NIP</FormLabel>
                <Stack spacing={2}>
                  <HStack align="start" spacing={2}>
                    <Input
                      size="sm"
                      value={form.nip ?? ""}
                      onChange={(event) => updateNIP(event.target.value)}
                      placeholder="5261040828"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      colorScheme="teal"
                      onClick={() => void handleGUSLookup()}
                      isLoading={isGUSLoading}
                      isDisabled={!canLookupGUS}
                      flexShrink={0}
                    >
                      Pobierz z GUS
                    </Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.500">
                    Wpisz poprawny 10-cyfrowy NIP, aby pobrać dane kontrahenta z
                    GUS.
                  </Text>
                </Stack>
                <FormErrorMessage>{errors.nip}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>VAT UE</FormLabel>
                <Input
                  size="sm"
                  value={form.vat_ue ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      vat_ue: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>REGON</FormLabel>
                <Input
                  size="sm"
                  value={form.regon ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      regon: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>KRS</FormLabel>
                <Input
                  size="sm"
                  value={form.krs ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      krs: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>BDO</FormLabel>
                <Input
                  size="sm"
                  inputMode="numeric"
                  placeholder="000012345"
                  value={form.bdo ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      bdo: event.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>EORI</FormLabel>
                <Input
                  size="sm"
                  value={form.eori ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      eori: event.target.value,
                    }))
                  }
                />
              </FormControl>
            </Grid>
            <Modal
              isOpen={isOpen}
              onClose={onClose}
              size="4xl"
              scrollBehavior="inside"
            >
              <ModalOverlay />
              <ModalContent borderRadius="2xl">
                <ModalHeader>
                  <VStack align="start" spacing={1}>
                    <Text>Weryfikacja danych z GUS</Text>
                    {gusLookupResult && (
                      <Badge
                        colorScheme={gusLookupResult.mocked ? "orange" : "teal"}
                        borderRadius="full"
                        px={3}
                      >
                        {gusLookupResult.mocked
                          ? "Tryb testowy (Mock)"
                          : "Dane oficjalne"}
                      </Badge>
                    )}
                  </VStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {gusLookupResult && (
                    <Stack spacing={4}>
                      <Box
                        p={4}
                        bg={gusBoxBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={gusBoxBorder}
                      >
                        <VStack align="stretch" spacing={2}>
                          <Text fontWeight="bold" fontSize="lg">
                            {gusLookupResult.company_name}
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <Box>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                NIP
                              </Text>
                              <Text fontWeight="medium">
                                {gusLookupResult.prefill.nip || "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                REGON
                              </Text>
                              <Text fontWeight="medium">
                                {gusLookupResult.prefill.regon || "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                KRS
                              </Text>
                              <Text fontWeight="medium">
                                {gusLookupResult.prefill.krs || "—"}
                              </Text>
                            </Box>
                          </SimpleGrid>
                        </VStack>
                      </Box>

                      <TableContainer borderWidth="1px" borderRadius="xl">
                        <Table variant="simple" size="sm">
                          <Thead bg={gusTableHeadBg}>
                            <Tr>
                              <Th>Pole</Th>
                              <Th>Obecna wartość</Th>
                              <Th>Nowa wartość (GUS)</Th>
                              <Th>Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {gusDiffRows.map((row) => (
                              <Tr
                                key={row.key}
                                bg={
                                  row.changed ? gusRowChangedBg : "transparent"
                                }
                              >
                                <Td fontWeight="medium">{row.label}</Td>
                                <Td color={gusMutedText}>
                                  {row.currentValue || "—"}
                                </Td>
                                <Td
                                  fontWeight={row.changed ? "bold" : "normal"}
                                >
                                  {row.nextValue || "—"}
                                </Td>
                                <Td>
                                  {row.changed ? (
                                    <Badge colorScheme="green" variant="subtle">
                                      Zmieni się
                                    </Badge>
                                  ) : (
                                    <Badge colorScheme="gray" variant="subtle">
                                      Bez zmian
                                    </Badge>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>

                      {gusLookupResult.mock_note && (
                        <Text
                          fontSize="sm"
                          color={gusMockNoteColor}
                          fontStyle="italic"
                        >
                          {gusLookupResult.mock_note}
                        </Text>
                      )}
                    </Stack>
                  )}
                </ModalBody>
                <ModalFooter gap={3}>
                  <Button onClick={onClose} variant="ghost">
                    Anuluj
                  </Button>
                  <Button colorScheme="teal" onClick={applyGUSPrefill}>
                    Potwierdź i zastosuj dane
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </TabPanel>

          <TabPanel px={0} pt={6}>
            <Stack spacing={8}>
              <Box>
                <HStack
                  justify="space-between"
                  mb={3}
                  align="center"
                  wrap="wrap"
                >
                  <Heading size="sm">Adresy</Heading>
                  <Button
                    leftIcon={<FiPlus />}
                    size="sm"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        addresses: [...current.addresses, emptyAddress()],
                      }))
                    }
                  >
                    Dodaj adres
                  </Button>
                </HStack>
                <Stack spacing={4}>
                  {form.addresses.map((address, index) => (
                    <Box
                      key={`address-${index}`}
                      borderWidth="1px"
                      borderRadius="md"
                      p={4}
                    >
                      <HStack
                        justify="space-between"
                        mb={3}
                        align="center"
                        wrap="wrap"
                      >
                        <Text fontWeight="semibold">Adres {index + 1}</Text>
                        <IconButton
                          aria-label="Usuń adres"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          isDisabled={form.addresses.length === 1}
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              addresses: current.addresses.filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            }))
                          }
                        />
                      </HStack>
                      <Grid
                        templateColumns={{
                          base: "minmax(0, 1fr)",
                          md: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(3, minmax(0, 1fr))",
                        }}
                        gap={4}
                      >
                        <FormControl>
                          <FormLabel>Typ adresu</FormLabel>
                          <Select
                            size="sm"
                            value={address.type}
                            onChange={(event) =>
                              updateAddress(index, {
                                type: event.target
                                  .value as ContragentAddressDTO["type"],
                              })
                            }
                          >
                            {ADDRESS_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Ulica</FormLabel>
                          <Input
                            size="sm"
                            value={address.street}
                            onChange={(event) =>
                              updateAddress(index, {
                                street: event.target.value,
                              })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Nr budynku</FormLabel>
                          <Input
                            size="sm"
                            value={address.building_number}
                            onChange={(event) =>
                              updateAddress(index, {
                                building_number: event.target.value,
                              })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Nr lokalu</FormLabel>
                          <Input
                            size="sm"
                            value={address.apartment_number ?? ""}
                            onChange={(event) =>
                              updateAddress(index, {
                                apartment_number: event.target.value,
                              })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Kod pocztowy</FormLabel>
                          <HStack
                            align="stretch"
                            flexDirection={{ base: "column", sm: "row" }}
                          >
                            <Input
                              size="sm"
                              value={address.postal_code}
                              onChange={(event) =>
                                updateAddress(index, {
                                  postal_code: event.target.value,
                                })
                              }
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void handlePostalCodeLookup(index)}
                            >
                              Pobierz
                            </Button>
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Miasto</FormLabel>
                          <Input
                            size="sm"
                            value={address.city}
                            onChange={(event) =>
                              updateAddress(index, { city: event.target.value })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Kraj</FormLabel>
                          <Input
                            size="sm"
                            value={address.country_code}
                            onChange={(event) =>
                              updateAddress(index, {
                                country_code: event.target.value.toUpperCase(),
                              })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>GLN</FormLabel>
                          <Input
                            size="sm"
                            value={address.gln ?? ""}
                            onChange={(event) =>
                              updateAddress(index, { gln: event.target.value })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>TERYT</FormLabel>
                          <Input
                            size="sm"
                            value={address.teryt_code ?? ""}
                            onChange={(event) =>
                              updateAddress(index, {
                                teryt_code: event.target.value,
                              })
                            }
                          />
                        </FormControl>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
                {errors.addresses ? (
                  <Text mt={2} color="red.500" fontSize="sm">
                    {errors.addresses}
                  </Text>
                ) : null}
              </Box>

              <Box>
                <HStack
                  justify="space-between"
                  mb={3}
                  align="center"
                  wrap="wrap"
                >
                  <Heading size="sm">Okna dostaw</Heading>
                  <Button
                    leftIcon={<FiPlus />}
                    size="sm"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        delivery_windows: [
                          ...current.delivery_windows,
                          emptyDeliveryWindow(),
                        ],
                      }))
                    }
                  >
                    Dodaj okno
                  </Button>
                </HStack>
                <Stack spacing={4}>
                  {form.delivery_windows.map((window, index) => (
                    <Box
                      key={`window-${index}`}
                      borderWidth="1px"
                      borderRadius="md"
                      p={4}
                    >
                      <HStack
                        justify="space-between"
                        mb={3}
                        align="center"
                        wrap="wrap"
                      >
                        <Text fontWeight="semibold">Okno {index + 1}</Text>
                        <IconButton
                          aria-label="Usuń okno"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          isDisabled={form.delivery_windows.length === 1}
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              delivery_windows: current.delivery_windows.filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            }))
                          }
                        />
                      </HStack>
                      <Grid
                        templateColumns={{
                          base: "minmax(0, 1fr)",
                          md: "repeat(3, minmax(0, 1fr))",
                        }}
                        gap={4}
                      >
                        <FormControl>
                          <FormLabel>Dzień</FormLabel>
                          <Select
                            size="sm"
                            value={window.day}
                            onChange={(event) =>
                              updateWindow(index, { day: event.target.value })
                            }
                          >
                            {DELIVERY_DAY_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Od</FormLabel>
                          <Input
                            size="sm"
                            type="time"
                            value={window.from}
                            onChange={(event) =>
                              updateWindow(index, { from: event.target.value })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Do</FormLabel>
                          <Input
                            size="sm"
                            type="time"
                            value={window.to}
                            onChange={(event) =>
                              updateWindow(index, { to: event.target.value })
                            }
                          />
                        </FormControl>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel px={0} pt={6}>
            <Stack spacing={8}>
              <Grid
                templateColumns={{
                  base: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                  xl: "repeat(4, minmax(0, 1fr))",
                }}
                gap={4}
              >
                <FormControl>
                  <FormLabel>Incoterms</FormLabel>
                  <Select
                    size="sm"
                    value={form.incoterms ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        incoterms: event.target.value,
                      }))
                    }
                  >
                    <option value="">Wybierz Incoterms</option>
                    {INCOTERMS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Waluta domyślna</FormLabel>
                  <Select
                    size="sm"
                    value={form.default_currency ?? "PLN"}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        default_currency: event.target.value,
                      }))
                    }
                  >
                    {CURRENCY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Termin płatności (dni)</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    value={form.payment_terms_days ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        payment_terms_days:
                          event.target.value === ""
                            ? undefined
                            : Number(event.target.value),
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Język</FormLabel>
                  <Select
                    size="sm"
                    value={form.preferred_language ?? "pl"}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        preferred_language: event.target.value,
                      }))
                    }
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Box>
                <HStack
                  justify="space-between"
                  mb={3}
                  align="center"
                  wrap="wrap"
                >
                  <Heading size="sm">Rachunki bankowe</Heading>
                  <HStack wrap="wrap">
                    <FormControl width="auto">
                      <FormLabel fontSize="xs" mb={1}>
                        Data sprawdzenia
                      </FormLabel>
                      <Input
                        aria-label="Data sprawdzenia na białej liście"
                        type="date"
                        size="sm"
                        max={today}
                        value={vatQueryDate}
                        onChange={(event) =>
                          setVATQueryDate(event.target.value)
                        }
                      />
                    </FormControl>
                    <Button
                      size="sm"
                      variant="outline"
                      isLoading={isVATLoading}
                      onClick={() => void searchVATAccounts()}
                    >
                      Pobierz rachunki z białej listy
                    </Button>
                    <Button
                      leftIcon={<FiPlus />}
                      size="sm"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          bank_accounts: [
                            ...current.bank_accounts,
                            emptyBankAccount(),
                          ],
                        }))
                      }
                    >
                      Dodaj rachunek
                    </Button>
                  </HStack>
                </HStack>
                <Stack spacing={4}>
                  {form.bank_accounts.map((account, index) => (
                    <Box
                      key={`bank-${index}`}
                      borderWidth="1px"
                      borderRadius="md"
                      p={4}
                    >
                      <HStack
                        justify="space-between"
                        mb={3}
                        align="center"
                        wrap="wrap"
                      >
                        <Text fontWeight="semibold">Rachunek {index + 1}</Text>
                        <IconButton
                          aria-label="Usuń rachunek"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          isDisabled={form.bank_accounts.length === 1}
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              bank_accounts: current.bank_accounts.filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            }))
                          }
                        />
                      </HStack>
                      <Grid
                        templateColumns={{
                          base: "minmax(0, 1fr)",
                          lg: "repeat(2, minmax(0, 1fr))",
                        }}
                        gap={4}
                      >
                        <FormControl>
                          <FormLabel>IBAN</FormLabel>
                          <Input
                            size="sm"
                            value={account.iban}
                            onChange={(event) =>
                              updateBank(index, { iban: event.target.value })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>SWIFT</FormLabel>
                          <Input
                            size="sm"
                            value={account.swift ?? ""}
                            onChange={(event) =>
                              updateBank(index, { swift: event.target.value })
                            }
                          />
                        </FormControl>
                      </Grid>
                      <HStack mt={3} justify="space-between" wrap="wrap">
                        {account.verification_request_id ? (
                          <Stack spacing={0} fontSize="sm">
                            <Badge
                              alignSelf="start"
                              colorScheme={
                                account.is_whitelisted ? "green" : "red"
                              }
                            >
                              {account.is_whitelisted
                                ? "Na białej liście"
                                : "Brak na białej liście"}
                            </Badge>
                            <Text color="gray.500">
                              Data: {account.verification_query_date} · ID:{" "}
                              {account.verification_request_id}
                            </Text>
                            <Text color="gray.500">
                              NIP: {account.verification_nip} · Sprawdzono:{" "}
                              {account.verified_at
                                ? new Date(account.verified_at).toLocaleString()
                                : "—"}
                            </Text>
                          </Stack>
                        ) : (
                          <Badge colorScheme="gray">Niezweryfikowany</Badge>
                        )}
                        {contragentId ? (
                          <HStack>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void loadVATAccountHistory(account.iban)
                              }
                            >
                              Historia
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              isLoading={verifyingAccount === account.iban}
                              onClick={() => void verifyVATAccount(index)}
                            >
                              Sprawdź na białej liście
                            </Button>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            Zapisz kontrahenta przed weryfikacją
                          </Text>
                        )}
                      </HStack>
                      {historyByAccount[account.iban] ? (
                        <Stack mt={3} pt={3} borderTopWidth="1px" spacing={1}>
                          {historyByAccount[account.iban].length === 0 ? (
                            <Text fontSize="sm" color="gray.500">
                              Brak historii weryfikacji
                            </Text>
                          ) : (
                            historyByAccount[account.iban].map((entry) => (
                              <Text key={entry.id} fontSize="sm">
                                <Badge
                                  mr={2}
                                  colorScheme={
                                    entry.account_assigned ? "green" : "red"
                                  }
                                >
                                  {entry.account_assigned ? "TAK" : "NIE"}
                                </Badge>
                                {entry.query_date} · NIP {entry.nip} ·{" "}
                                {new Date(entry.checked_at).toLocaleString()} ·{" "}
                                {entry.request_id}
                              </Text>
                            ))
                          )}
                        </Stack>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Modal
                isOpen={vatModal.isOpen}
                onClose={vatModal.onClose}
                size="xl"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Rachunki z białej listy VAT</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Stack spacing={3}>
                      {vatResult ? (
                        <>
                          <Text fontWeight="bold">{vatResult.name}</Text>
                          <Text fontSize="sm">
                            Status VAT: {vatResult.status_vat} · ID zapytania:{" "}
                            {vatResult.request_id}
                          </Text>
                          {vatResult.has_virtual_accounts ? (
                            <Badge colorScheme="orange">
                              Podmiot używa rachunków wirtualnych
                            </Badge>
                          ) : null}
                          {vatResult.account_numbers.map((iban) => {
                            const duplicate = form.bank_accounts.some(
                              (account) =>
                                account.iban
                                  .replace(/[\s-]/g, "")
                                  .toUpperCase() ===
                                iban.replace(/[\s-]/g, "").toUpperCase(),
                            )
                            return (
                              <Checkbox
                                key={iban}
                                isDisabled={duplicate}
                                isChecked={vatSelected.includes(iban)}
                                onChange={(event) =>
                                  setVATSelected((current) =>
                                    event.target.checked
                                      ? [...current, iban]
                                      : current.filter((item) => item !== iban),
                                  )
                                }
                              >
                                {iban}
                                {duplicate ? " (już dodany)" : ""}
                              </Checkbox>
                            )
                          })}
                        </>
                      ) : null}
                    </Stack>
                  </ModalBody>
                  <ModalFooter gap={2}>
                    <Button size="sm" onClick={vatModal.onClose}>
                      Anuluj
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="green"
                      isDisabled={vatSelected.length === 0}
                      onClick={addSelectedVATAccounts}
                    >
                      Dodaj wybrane
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </Stack>
          </TabPanel>
          {extraTabs.map((tab) => (
            <TabPanel key={tab.key} px={0} pt={6}>
              {tab.content}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {showSubmitActions ? (
        <HStack justify="flex-end" wrap="wrap">
          {onCancel ? (
            <Button size="sm" onClick={onCancel}>
              Anuluj
            </Button>
          ) : null}
          <Button
            size="sm"
            colorScheme="green"
            type="submit"
            isLoading={isPending}
          >
            {submitLabel}
          </Button>
        </HStack>
      ) : null}
    </VStack>
  )
}
